import { useMemo } from 'react';
import {
  hasAnyPermission,
  hasPermission,
  type PermissionKey,
  type RoleLike,
} from '@construction/shared';
import { useAuthStore } from '@/store/auth';

// 文案映射单独放 permission-labels.ts（那边不引 store，测试能直接 import）
export {
  PERMISSION_GROUP_LABEL_KEY,
  PERMISSION_HINT_KEY,
  PERMISSION_LABEL_KEY,
} from './permission-labels';

export interface Permissions {
  role: RoleLike | null;
  can: (key: PermissionKey) => boolean;
  canAny: (keys: readonly PermissionKey[]) => boolean;
  /** 超管那条系统角色 */
  isSystem: boolean;
  /** 归哪家（CR-073）；null = 平台账号 */
  orgId: number | null;
  /**
   * 平台账号：看得到所有单位。只有它的界面上出现「公司」列、筛选和下拉 ——
   * 单位管理员看到的就是「全部」，范围已经被服务端圈好了（CR-073）
   */
  isPlatform: boolean;
}

/**
 * 当前登录人的权限（CR-038）。页面里「这个按钮显不显示」一律问它，别自己比角色名 ——
 * 角色名是客户随便改的数据，`=== '直属工头'` 这种判断第二天就失效。
 *
 * **哪些页面有入口、进不进得去不在这里判断**，那是路由表的事（CR-040，`lib/routes/`）。
 * 这里只管页面里面的按钮和查询。
 *
 * 这一层是体验保护，不是安全边界：真正挡住人的是服务端的三层校验（CLAUDE.md 约束 7）。
 */
export function usePermissions(): Permissions {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const orgId = useAuthStore((s) => s.user?.orgId ?? null);
  return useMemo(
    () => ({
      role,
      can: (key) => hasPermission(role, key),
      canAny: (keys) => hasAnyPermission(role, keys),
      isSystem: role?.isSystem ?? false,
      orgId,
      // 没登录（role 为空）不算平台账号 —— orgId 为空不等于「看得到全部」
      isPlatform: role !== null && orgId === null,
    }),
    [role, orgId],
  );
}
